import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import { QuizBuilderModal } from './QuizBuilderModal';
import { DeletePlanConfirmModal } from './DeletePlanConfirmModal';
import { LaunchDisplayModal } from '../pages-daily-briefing/LaunchDisplayModal';
import { StyleBadge } from '../pages-daily-briefing/TodaysBriefingCard';
import {
    Btn,
    StatsGrid,
    IconCheckCircle,
    IconImage,
    IconPdf,
    IconPencil,
    IconPlay,
    IconVideo,
} from '../ui';
import type {
    SafetyTrainingView,
    TrainingPlan,
    PlanCategory,
    PlanStatus,
    ComplianceStatus,
    TrainingEnrollment,
} from '../../types/trainingTypes';
import { TOPIC_STYLE_COLORS, type TopicStyle } from '../../types/trainingTypes';
import { calculateCategoryProgress, parseLocalDateStr } from '../../utils/trainingUtils';
import { hasPermission, Permission } from '../../utils/permissions';
import {
    listBriefingTopics,
    findTodaysTopic,
    buildCompanyScheduleDraft,
    getConfirmationStats,
    getTodayDateKey,
    formatBriefingDate,
    type BriefingTopic,
    type ConfirmationRow,
    type ScheduleDraftEntry,
} from '../pages-daily-briefing/dailyBriefingData';
import '../../css/AdminDashboardView.css';
import '../../css/DailyBriefing.css';

function TopicStyleMediaIcon({ style }: { style: TopicStyle | BriefingTopic['style'] }) {
    if (style === 'Video') return <IconVideo />;
    if (style === 'PDF') return <IconPdf />;
    if (style === 'Poster') return <IconImage />;
    return <IconPlay />;
}

interface AdminDashboardViewProps {
    onNavigate: (view: SafetyTrainingView) => void;
    onOpenActionLauncher: (planId?: string, planName?: string) => void;
    templateMode?: boolean;
}

/**
 * Admin Dashboard View — plan-contextual dashboard showing compliance statistics,
 * today's training topic, team confirmations, category progress, and user compliance.
 *
 * Active plan state: stat cards, today's topic, team confirmations, coming up, category progress, user compliance table.
 * Draft plan state: dashes for stats, "Plan not yet activated", 0% progress, "No users enrolled" placeholder.
 *
 * Validates: Requirements 8.1–8.12, 9.1–9.6
 */
export const AdminDashboardView = ({
    onNavigate,
    onOpenActionLauncher,
    templateMode = false,
}: AdminDashboardViewProps) => {
    const {
        plans,
        topics,
        schedules,
        completions,
        selectedPlanId,
        setSelectedPlanId,
        getEnrolledUsers,
        getUserComplianceStatus,
        getUserCompletions,
        updatePlan,
        deletePlan,
    } = useSafetyTrainingContext();

    const navigate = useNavigate();
    const { companyUser, companyOwner, allCompanyUsers, businessAdmin } = useUserContext();
    const currentUserId = companyUser?.id || companyOwner?.id || '';
    const userCompanyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';
    const companyId = templateMode && !userCompanyId ? 'library-template' : userCompanyId;
    const canScheduleBriefing = hasPermission(
        companyOwner,
        companyUser,
        Permission.MANAGE_SAFETY_AND_TRAINING,
        businessAdmin,
    );

    // Daily Briefing schedule (assignedSafetyLesson) — powers Today’s Topic + Coming Up
    const [briefingTopics, setBriefingTopics] = useState<BriefingTopic[]>([]);
    const [briefingLoading, setBriefingLoading] = useState(false);

    useEffect(() => {
        if (templateMode || !companyId || companyId === 'library-template') {
            setBriefingTopics([]);
            return;
        }
        let isCancelled = false;
        setBriefingLoading(true);
        (async () => {
            try {
                const list = await listBriefingTopics(companyId);
                if (isCancelled) return;
                setBriefingTopics(list);
            } catch (err) {
                console.error('Failed to load Daily Briefing topics for dashboard', err);
                if (!isCancelled) setBriefingTopics([]);
            } finally {
                if (!isCancelled) setBriefingLoading(false);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [templateMode, companyId]);

    // Determine selected plan (default to first plan if none selected)
    const selectedPlan = useMemo<TrainingPlan | null>(() => {
        if (selectedPlanId) return plans.find((p) => p.id === selectedPlanId) ?? null;
        // Default: first active plan, or first plan
        const active = plans.find((p) => p.status === 'ACTIVE');
        return active ?? plans[0] ?? null;
    }, [plans, selectedPlanId]);

    const isActivePlan = selectedPlan?.status === 'ACTIVE';
    const isDraftPlan = selectedPlan?.status === 'DRAFT';

    // Parse categories from selected plan
    const categories = useMemo<PlanCategory[]>(() => {
        if (!selectedPlan) return [];
        try {
            return JSON.parse(selectedPlan.categories) as PlanCategory[];
        } catch {
            return [];
        }
    }, [selectedPlan]);

    // Enrolled users for selected plan
    const planEnrollments = useMemo<TrainingEnrollment[]>(() => {
        if (!selectedPlan) return [];
        return getEnrolledUsers(selectedPlan.id);
    }, [selectedPlan, getEnrolledUsers]);

    // Today's date key (shared with Daily Briefing scheduling)
    const todayStr = useMemo(() => getTodayDateKey(), []);

    // Today’s topic from Daily Briefing assignedSafetyLesson
    const briefingTodayTopic = useMemo(
        () => (companyId && companyId !== 'library-template' ? findTodaysTopic(briefingTopics, companyId, todayStr) : null),
        [briefingTopics, companyId, todayStr],
    );

    // Coming up: future Daily Briefing assignments (next 5)
    const comingUpBriefings = useMemo<ScheduleDraftEntry[]>(() => {
        if (!companyId || companyId === 'library-template') return [];
        const draft = buildCompanyScheduleDraft(briefingTopics, companyId);
        return Object.values(draft)
            .filter((entry) => entry.dateKey > todayStr)
            .sort((a, b) => a.dateKey.localeCompare(b.dateKey))
            .slice(0, 5);
    }, [briefingTopics, companyId, todayStr]);

    // Team confirmations from Daily Briefing safetyLessons (crew who confirmed today’s topic)
    const confirmationStats = useMemo(() => {
        if (!briefingTodayTopic) return null;
        return getConfirmationStats(allCompanyUsers ?? [], briefingTodayTopic.id, todayStr);
    }, [briefingTodayTopic, allCompanyUsers, todayStr]);

    const teamConfirmations = useMemo(() => {
        if (!confirmationStats) {
            return { confirmed: [] as ConfirmationRow[], pending: [] as ConfirmationRow[] };
        }
        return {
            confirmed: confirmationStats.rows.filter((r) => r.confirmed),
            pending: confirmationStats.rows.filter((r) => !r.confirmed),
        };
    }, [confirmationStats]);

    const myConfirmation = useMemo(() => {
        if (!confirmationStats || !currentUserId) return null;
        return confirmationStats.rows.find((r) => r.userId === currentUserId && r.confirmed) ?? null;
    }, [confirmationStats, currentUserId]);

    // Plan day N of cycle (plan schedule) — shown in today’s topic header when available
    const planDayLabel = useMemo(() => {
        if (!selectedPlan?.cycleDuration || !isActivePlan) return null;
        const todaySched = schedules.find(
            (s) => s.planId === selectedPlan.id && s.scheduledDate.split('T')[0] === todayStr,
        );
        if (todaySched?.dayNumber) {
            return `Plan day ${todaySched.dayNumber} of ${selectedPlan.cycleDuration}`;
        }
        return null;
    }, [selectedPlan, isActivePlan, schedules, todayStr]);

    const todayHeaderMeta = useMemo(() => {
        const parts = [formatBriefingDate()];
        if (planDayLabel) parts.push(planDayLabel);
        return parts.join(' • ');
    }, [planDayLabel]);

    // Compliance stats for active plan
    const complianceStats = useMemo(() => {
        if (!selectedPlan || !isActivePlan) {
            return {
                overallPercent: 0,
                onTrackCount: 0,
                behindCount: 0,
                daysUntilCycle: 0,
                nextDueDate: '',
                topicsThisWeek: 0,
                avgTopicTime: 0,
            };
        }

        let onTrackCount = 0;
        let behindCount = 0;
        planEnrollments.forEach((e) => {
            const status = getUserComplianceStatus(e.userId, selectedPlan.id);
            if (status === 'on-track') onTrackCount++;
            else behindCount++;
        });

        const totalEnrolled = planEnrollments.length;
        const overallPercent = totalEnrolled > 0 ? Math.round((onTrackCount / totalEnrolled) * 100) : 0;

        // Days until cycle end — use first enrollment's enrolled date + cycleDuration
        let daysUntilCycle = 0;
        let nextDueDate = '';
        if (planEnrollments.length > 0) {
            const firstEnrollment = planEnrollments[0];
            if (firstEnrollment.nextDueDate) {
                const due = parseLocalDateStr(firstEnrollment.nextDueDate);
                const now = new Date();
                daysUntilCycle = Math.max(0, Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
                nextDueDate = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            } else {
                const enrolled = parseLocalDateStr(firstEnrollment.enrolledDate);
                const cycleEnd = new Date(enrolled);
                cycleEnd.setDate(cycleEnd.getDate() + selectedPlan.cycleDuration);
                const now = new Date();
                daysUntilCycle = Math.max(0, Math.ceil((cycleEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
                nextDueDate = cycleEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
        }

        // Topics this week
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        const startStr = `${startOfWeek.getFullYear()}-${String(startOfWeek.getMonth() + 1).padStart(2, '0')}-${String(startOfWeek.getDate()).padStart(2, '0')}`;
        const endStr = `${endOfWeek.getFullYear()}-${String(endOfWeek.getMonth() + 1).padStart(2, '0')}-${String(endOfWeek.getDate()).padStart(2, '0')}`;

        const weekSchedules = schedules.filter((s) => {
            if (s.planId !== selectedPlan.id) return false;
            const d = s.scheduledDate.split('T')[0];
            return d >= startStr && d <= endStr;
        });

        const weekTopicTimes = weekSchedules.map((s) => {
            const t = topics.find((tp) => tp.topicId === s.topicId || tp.id === s.topicId);
            return t?.timeValue ?? 0;
        });
        const avgTopicTime =
            weekTopicTimes.length > 0
                ? Math.round(weekTopicTimes.reduce((a, b) => a + b, 0) / weekTopicTimes.length)
                : 0;

        return {
            overallPercent,
            onTrackCount,
            behindCount,
            daysUntilCycle,
            nextDueDate,
            topicsThisWeek: weekSchedules.length,
            avgTopicTime,
        };
    }, [selectedPlan, isActivePlan, planEnrollments, getUserComplianceStatus, schedules, topics]);

    // Category progress (aggregate across all enrolled users for the plan)
    const categoryProgress = useMemo(() => {
        if (!selectedPlan || categories.length === 0) return [];
        const planCompletions = completions.filter((c) => c.planId === selectedPlan.id);
        return calculateCategoryProgress(categories, planCompletions, topics);
    }, [selectedPlan, categories, completions, topics]);

    // Content stats per category: how much content is assigned (uploaded) and how much is scheduled
    const categoryContentStats = useMemo(() => {
        if (!selectedPlan || categories.length === 0) return [];
        const planSchedules = schedules.filter((s) => s.planId === selectedPlan.id);
        return categories.map((cat) => {
            // Topics assigned to this category via categoryIds
            const assignedTopics = topics.filter(
                (t) => t.categoryIds?.includes(cat.id) && t.planIds?.includes(selectedPlan.id),
            );
            const assignedMinutes = assignedTopics.reduce((sum, t) => sum + t.timeValue, 0);
            // Topics in this category that are on the schedule
            const scheduledTopicIds = new Set(
                planSchedules.filter((s) => s.categoryId === cat.id).map((s) => s.topicId),
            );
            const scheduledMinutes = assignedTopics
                .filter((t) => scheduledTopicIds.has(t.id))
                .reduce((sum, t) => sum + t.timeValue, 0);
            const unscheduledMinutes = assignedMinutes - scheduledMinutes;
            const unscheduledCount = assignedTopics.filter((t) => !scheduledTopicIds.has(t.id)).length;
            return {
                categoryId: cat.id,
                assignedTopicCount: assignedTopics.length,
                assignedMinutes,
                scheduledMinutes,
                unscheduledMinutes,
                unscheduledCount,
                assignedPct: cat.targetMinutes > 0 ? Math.min(100, (assignedMinutes / cat.targetMinutes) * 100) : 0,
                scheduledPct: cat.targetMinutes > 0 ? Math.min(100, (scheduledMinutes / cat.targetMinutes) * 100) : 0,
            };
        });
    }, [selectedPlan, categories, topics, schedules]);

    // User compliance data for the table
    const userComplianceData = useMemo(() => {
        if (!selectedPlan) return [];
        return planEnrollments.map((enrollment) => {
            const user = allCompanyUsers.find((u) => u.id === enrollment.userId);
            const status = getUserComplianceStatus(enrollment.userId, selectedPlan.id);
            const userCompletions = getUserCompletions(enrollment.userId, selectedPlan.id);
            const totalMinutesViewed = userCompletions.reduce((sum, c) => sum + c.timeValueMinutes, 0);
            const lastActivity =
                userCompletions.length > 0
                    ? userCompletions.sort((a, b) => b.completionDate.localeCompare(a.completionDate))[0].completionDate
                    : null;
            const progressPercent =
                selectedPlan.minimumTimeRequired > 0
                    ? Math.min(100, Math.round((totalMinutesViewed / selectedPlan.minimumTimeRequired) * 100))
                    : 0;

            return {
                enrollment,
                user,
                status,
                totalMinutesViewed,
                lastActivity,
                progressPercent,
                isCurrentUser: enrollment.userId === currentUserId,
            };
        });
    }, [selectedPlan, planEnrollments, allCompanyUsers, getUserComplianceStatus, getUserCompletions, currentUserId]);

    // Activate plan handler
    const [isActivating, setIsActivating] = useState(false);
    const [showContentViewer, setShowContentViewer] = useState(false);
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Draft category editing state
    const [editingCategories, setEditingCategories] = useState<PlanCategory[] | null>(null);
    const [isSavingCategories, setIsSavingCategories] = useState(false);
    const isEditingCategories = editingCategories !== null;
    const [quizBuilderCategory, setQuizBuilderCategory] = useState<PlanCategory | null>(null);
    const [confirmDisableQuizCategory, setConfirmDisableQuizCategory] = useState<PlanCategory | null>(null);

    const startEditingCategories = useCallback(() => {
        setEditingCategories([...categories]);
    }, [categories]);

    const cancelEditingCategories = useCallback(() => {
        setEditingCategories(null);
    }, []);

    const addEditCategory = useCallback(() => {
        setEditingCategories((prev) => {
            if (!prev) return prev;
            const id =
                typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `cat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            return [...prev, { id, name: '', targetMinutes: 0 }];
        });
    }, []);

    const updateEditCategory = useCallback(
        (id: string, field: keyof PlanCategory, value: string | number | boolean) => {
            setEditingCategories((prev) => {
                if (!prev) return prev;
                return prev.map((c) => (c.id === id ? { ...c, [field]: value } : c));
            });
        },
        [],
    );

    const removeEditCategory = useCallback((id: string) => {
        setEditingCategories((prev) => {
            if (!prev) return prev;
            return prev.filter((c) => c.id !== id);
        });
    }, []);

    const saveCategories = useCallback(async () => {
        if (!selectedPlan || !editingCategories) return;
        setIsSavingCategories(true);
        try {
            await updatePlan({
                id: selectedPlan.id,
                categories: JSON.stringify(editingCategories),
            });
            setEditingCategories(null);
        } catch (err) {
            console.error('Failed to save categories:', err);
        } finally {
            setIsSavingCategories(false);
        }
    }, [selectedPlan, editingCategories, updatePlan]);
    const handleActivatePlan = useCallback(async () => {
        if (!selectedPlan || selectedPlan.status !== 'DRAFT') return;
        const confirmed = window.confirm(
            `Activate "${selectedPlan.name}"?\n\nThis will start the training cycle and begin tracking compliance for all enrolled users. This action cannot be undone.`,
        );
        if (!confirmed) return;
        setIsActivating(true);
        try {
            // Set startDate to today when activating from the dashboard
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const d = String(now.getDate()).padStart(2, '0');
            const todayDateStr = `${y}-${m}-${d}`;
            await updatePlan({
                id: selectedPlan.id,
                status: 'ACTIVE',
                startDate: selectedPlan.startDate ?? todayDateStr,
            });
        } catch (err) {
            console.error('Failed to activate plan:', err);
        } finally {
            setIsActivating(false);
        }
    }, [selectedPlan, updatePlan]);

    // Save as template handler — publishes the plan to the library with full topic bundling
    const handleSaveAsTemplate = useCallback(async () => {
        if (!selectedPlan) return;
        setIsSavingTemplate(true);
        try {
            const { saveAsTemplate } = await import('../../utils/templateUtils');
            const adminId = businessAdmin?.id || companyUser?.id || companyOwner?.id || '';
            const adminName = businessAdmin?.name || companyUser?.name || companyOwner?.name || 'Admin';

            // Get schedule entries for this plan
            const planSchedules = schedules.filter((s) => s.planId === selectedPlan.id);

            const result = await saveAsTemplate({
                plan: selectedPlan,
                topics,
                schedules: planSchedules,
                adminId,
                adminName,
            });

            alert(`"${selectedPlan.name}" saved to library with ${result.topicTemplateCount} topics.`);
        } catch (err) {
            console.error('Failed to save template:', err);
            alert('Failed to save template. Please try again.');
        } finally {
            setIsSavingTemplate(false);
        }
    }, [selectedPlan, topics, schedules, businessAdmin, companyUser, companyOwner]);

    // Helpers
    const getInitials = (name: string) => {
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : (name[0] ?? '').toUpperCase();
    };

    const getStatusBadgeLabel = (status: PlanStatus): string => {
        switch (status) {
            case 'ACTIVE':
                return 'Active';
            case 'DRAFT':
                return 'Draft';
            case 'COMPLETED':
                return 'Completed';
            default:
                return status;
        }
    };

    const getComplianceBadgeLabel = (status: ComplianceStatus): string => {
        switch (status) {
            case 'on-track':
                return 'On Track';
            case 'behind':
                return 'Behind';
            case 'at-risk':
                return '⚠ At Risk';
            default:
                return status;
        }
    };

    const formatDate = (dateStr: string): string => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    if (!selectedPlan && plans.length === 0) {
        return (
            <div className="adv-draft-placeholder">
                {templateMode
                    ? 'No template plans yet. Click "Manage Plans" above to create your first plan.'
                    : 'No training plans yet. Click "Manage Plans" above to create your first plan.'}
            </div>
        );
    }

    return (
        <>
            {/* Plan Switcher Tabs */}
            <div className="adv-plan-switcher">
                {plans.map((plan) => (
                    <button
                        key={plan.id}
                        className={`adv-plan-tab ${selectedPlan?.id === plan.id ? 'active' : ''}`}
                        onClick={() => setSelectedPlanId(plan.id)}
                    >
                        {plan.name}
                        <span className={`adv-plan-status-badge ${plan.status.toLowerCase()}`}>
                            {getStatusBadgeLabel(plan.status)}
                        </span>
                        {plan.sourceTemplateId && (
                            <span
                                style={{
                                    fontSize: 9,
                                    background: '#ede9fe',
                                    color: '#6d28d9',
                                    padding: '1px 5px',
                                    borderRadius: 4,
                                    fontWeight: 600,
                                    marginLeft: 4,
                                }}
                            >
                                📥
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Plan-level header — shows selected plan info and plan-specific actions */}
            <div className="adv-header-row">
                <div className="adv-header-left">
                    <div className="adv-subtitle">
                        {isDraftPlan ? 'Viewing draft plan' : (selectedPlan?.name ?? '')}
                        {selectedPlan?.sourceTemplateId && (
                            <span
                                style={{
                                    fontSize: 11,
                                    background: '#ede9fe',
                                    color: '#6d28d9',
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontWeight: 600,
                                    marginLeft: 8,
                                }}
                            >
                                📥 Imported from Library
                            </span>
                        )}
                    </div>
                </div>
                <div className="adv-header-actions">
                    {!templateMode && isDraftPlan && (
                        <button
                            className="adv-btn-new-action"
                            style={{ background: '#16a34a' }}
                            onClick={handleActivatePlan}
                            disabled={isActivating}
                        >
                            {isActivating ? 'Activating...' : '✓ Activate Plan'}
                        </button>
                    )}
                    {templateMode ? (
                        <>
                            <button
                                className="adv-btn-manage"
                                onClick={() => onNavigate({ type: 'topics', filterPlanId: selectedPlan?.id })}
                            >
                                📋 Topic List
                            </button>
                            <button
                                className="adv-btn-manage"
                                onClick={() => onNavigate({ type: 'schedule-builder', planId: selectedPlan?.id })}
                            >
                                📅 Manage Schedule
                            </button>
                            <button
                                className="adv-btn-new-action"
                                style={{ background: '#4f46e5' }}
                                onClick={handleSaveAsTemplate}
                                disabled={isSavingTemplate}
                            >
                                {isSavingTemplate ? 'Saving...' : '📤 Save as Template'}
                            </button>
                        </>
                    ) : (
                        <button
                            className="adv-btn-new-action"
                            onClick={() => onOpenActionLauncher(selectedPlan?.id, selectedPlan?.name)}
                        >
                            + Plan Actions
                        </button>
                    )}
                    {isDraftPlan && (
                        <button
                            className="adv-btn-new-action"
                            style={{ background: '#dc2626' }}
                            onClick={() => setShowDeleteModal(true)}
                        >
                            🗑 Delete
                        </button>
                    )}
                </div>
            </div>

            {/* Stat Cards — hidden in template mode */}
            {!templateMode && (
                <StatsGrid
                    variant="loose"
                    className="adv-stat-cards"
                    stats={[
                        <div key="compliance" className={`adv-stat-card ${isDraftPlan ? 'draft' : ''}`}>
                            <div className="adv-stat-card-label">Overall Compliance</div>
                            <div className="adv-stat-card-value">
                                {isActivePlan ? `${complianceStats.overallPercent}%` : '—'}
                            </div>
                            <div className="adv-stat-card-detail">
                                {isActivePlan
                                    ? `${complianceStats.onTrackCount} of ${planEnrollments.length} on track`
                                    : 'Plan not yet activated'}
                            </div>
                        </div>,
                        <div key="days" className={`adv-stat-card ${isDraftPlan ? 'draft' : ''}`}>
                            <div className="adv-stat-card-label">Days Until Cycle</div>
                            <div className="adv-stat-card-value">
                                {isActivePlan ? complianceStats.daysUntilCycle : '—'}
                            </div>
                            <div className="adv-stat-card-detail">
                                {isActivePlan
                                    ? `Next due ${complianceStats.nextDueDate}`
                                    : 'Plan not yet activated'}
                            </div>
                        </div>,
                        <div key="behind" className={`adv-stat-card ${isDraftPlan ? 'draft' : ''}`}>
                            <div className="adv-stat-card-label">Behind Schedule</div>
                            <div className="adv-stat-card-value">
                                {isActivePlan ? complianceStats.behindCount : '—'}
                            </div>
                            <div className="adv-stat-card-detail">
                                {isActivePlan
                                    ? `Below 60% target`
                                    : 'Plan not yet activated'}
                            </div>
                        </div>,
                        <div key="topics" className={`adv-stat-card ${isDraftPlan ? 'draft' : ''}`}>
                            <div className="adv-stat-card-label">Topics This Week</div>
                            <div className="adv-stat-card-value">
                                {isActivePlan ? complianceStats.topicsThisWeek : '—'}
                            </div>
                            <div className="adv-stat-card-detail">
                                {isActivePlan
                                    ? `Avg ${complianceStats.avgTopicTime} min`
                                    : 'Plan not yet activated'}
                            </div>
                        </div>,
                    ]}
                />
            )}

            {/* Today's Topic card — topic + team confirmations + Coming Up (Figma) */}
            {!templateMode && (
                <div className="adv-today-topic-card">
                    <div className="adv-today-topic-header">
                        <h3>Today&apos;s Training Topic</h3>
                        <span className="adv-today-topic-header-meta">{todayHeaderMeta}</span>
                    </div>

                    {briefingTodayTopic ? (
                        <div className="adv-today-topic-main">
                            <div className="adv-today-topic-panel">
                                <div className="adv-today-topic-body">
                                    <div
                                        className="adv-topic-style-icon"
                                        style={{
                                            background:
                                                TOPIC_STYLE_COLORS[briefingTodayTopic.style as TopicStyle] ||
                                                '#6b7280',
                                            color: 'var(--white)',
                                        }}
                                    >
                                        <TopicStyleMediaIcon style={briefingTodayTopic.style} />
                                    </div>
                                    <div className="adv-topic-info">
                                        <div className="adv-topic-eyebrow">My Topic Today</div>
                                        <div className="adv-topic-title">{briefingTodayTopic.title}</div>
                                        <div className="adv-topic-meta">
                                            <StyleBadge style={briefingTodayTopic.style} />
                                            <span className="adv-topic-meta-sep" aria-hidden="true">
                                                ·
                                            </span>
                                            <span>{briefingTodayTopic.displayId}</span>
                                            {briefingTodayTopic.folderName?.trim() ? (
                                                <>
                                                    <span className="adv-topic-meta-sep" aria-hidden="true">
                                                        ·
                                                    </span>
                                                    <span>{briefingTodayTopic.folderName.trim()}</span>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                                <div className="adv-topic-actions">
                                    <Btn
                                        variant="success-outlined"
                                        buttonSize="sm"
                                        buttonText="Mark as Viewed"
                                        buttonIcon={<IconCheckCircle />}
                                        buttonOnClick={() => setShowContentViewer(true)}
                                        buttonClasses="adv-btn-mark-viewed"
                                    />
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonSize="sm"
                                        buttonText="Launch"
                                        buttonIcon={<IconPlay />}
                                        buttonOnClick={() => setShowContentViewer(true)}
                                        buttonClasses="adv-btn-launch"
                                    />
                                </div>
                                {myConfirmation && (
                                    <p className="adv-today-recorded">
                                        <IconCheckCircle />
                                        <span>
                                            Recorded for {myConfirmation.name}
                                            {myConfirmation.dateCompleted
                                                ? ` — ${new Date(myConfirmation.dateCompleted).toLocaleString(
                                                      'en-US',
                                                      {
                                                          month: 'short',
                                                          day: 'numeric',
                                                          year: 'numeric',
                                                          hour: 'numeric',
                                                          minute: '2-digit',
                                                      },
                                                  )}`
                                                : ''}
                                        </span>
                                    </p>
                                )}
                            </div>

                            <div className="adv-today-topic-divider" aria-hidden="true" />

                            <div className="adv-team-confirmations adv-team-confirmations--embedded">
                                <div className="adv-team-confirmations-header">
                                    <h3>Team Confirmations Today</h3>
                                    <div className="adv-team-confirmations-counts">
                                        <span className="adv-team-count adv-team-count--confirmed">
                                            <IconCheckCircle />
                                            {teamConfirmations.confirmed.length}
                                        </span>
                                        {teamConfirmations.pending.length > 0 && (
                                            <span className="adv-team-count adv-team-count--pending">
                                                {teamConfirmations.pending.length} Pending
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="adv-team-avatars">
                                    {teamConfirmations.confirmed.map((row) => (
                                        <div
                                            key={row.userId}
                                            className="adv-team-pill adv-team-pill--confirmed"
                                            title={row.name}
                                        >
                                            <span className="adv-team-pill-dot" aria-hidden="true" />
                                            <span className="adv-team-pill-initials">
                                                {getInitials(row.name)}
                                                {row.userId === currentUserId ? (
                                                    <span className="adv-team-pill-you"> you</span>
                                                ) : null}
                                            </span>
                                        </div>
                                    ))}
                                    {teamConfirmations.pending.map((row) => (
                                        <div
                                            key={row.userId}
                                            className="adv-team-pill adv-team-pill--pending"
                                            title={row.name}
                                        >
                                            <span className="adv-team-pill-dot" aria-hidden="true" />
                                            <span className="adv-team-pill-initials">
                                                {getInitials(row.name)}
                                                {row.userId === currentUserId ? (
                                                    <span className="adv-team-pill-you"> you</span>
                                                ) : null}
                                            </span>
                                        </div>
                                    ))}
                                    {teamConfirmations.confirmed.length === 0 &&
                                        teamConfirmations.pending.length === 0 && (
                                            <p className="adv-team-empty">No crew members yet</p>
                                        )}
                                </div>
                                {canScheduleBriefing && (
                                    <div className="adv-team-confirmations-footer">
                                        <Btn
                                            variant="secondary-outlined"
                                            buttonSize="sm"
                                            buttonText="Edit Today's Topic"
                                            buttonIcon={<IconPencil />}
                                            buttonOnClick={() => navigate('/daily-briefing')}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="adv-today-topic-placeholder">
                            {briefingLoading ? 'Loading…' : 'No topic scheduled for today'}
                        </div>
                    )}

                    <div className="adv-coming-up adv-coming-up--embedded">
                        <span className="adv-coming-up-label">Coming Up</span>
                        <div className="adv-coming-up-scroll">
                            {comingUpBriefings.length > 0 ? (
                                comingUpBriefings.map((entry) => {
                                    const weekday = new Date(`${entry.dateKey}T00:00:00`).toLocaleDateString(
                                        'en-US',
                                        { weekday: 'short' },
                                    );
                                    return (
                                        <div key={entry.dateKey} className="adv-coming-up-chip">
                                            <span className="adv-coming-up-chip-day">{weekday}</span>
                                            <span className="adv-coming-up-chip-title">{entry.title}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <span className="adv-coming-up-empty">
                                    {briefingLoading ? 'Loading…' : 'No upcoming topics scheduled'}
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            className="adv-view-schedule-link"
                            onClick={() => navigate('/daily-briefing')}
                        >
                            View full schedule →
                        </button>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="adv-main-content">
                {/* Left Column — hidden in template mode (user-facing content) */}
                {!templateMode && (
                    <div className="adv-left-column">
                        {/* User Compliance Table */}
                        <div className="adv-user-compliance">
                            <div className="adv-user-compliance-header">
                                <h3>User Compliance</h3>
                            </div>
                            {userComplianceData.length === 0 ? (
                                <div className="adv-draft-placeholder">
                                    No users enrolled in this plan yet.{' '}
                                    <span
                                        className="adv-enroll-link"
                                        onClick={() => onNavigate({ type: 'enrollment', planId: selectedPlan?.id })}
                                    >
                                        Enroll users →
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <table className="adv-compliance-table">
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Progress</th>
                                                <th>Viewed ITD</th>
                                                <th>Last Activity</th>
                                                <th>Next Due</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {userComplianceData.map((row) => (
                                                <tr
                                                    key={row.enrollment.id}
                                                    onClick={() =>
                                                        onNavigate({
                                                            type: 'user-profile',
                                                            userId: row.enrollment.userId,
                                                        })
                                                    }
                                                >
                                                    <td>
                                                        <div className="adv-user-cell">
                                                            <div className="adv-user-avatar">
                                                                {getInitials(row.user?.name ?? 'U')}
                                                            </div>
                                                            <div className="adv-user-name-group">
                                                                <span className="adv-user-name">
                                                                    {row.user?.name ?? 'Unknown User'}
                                                                    {row.isCurrentUser && (
                                                                        <span className="adv-you-badge">YOU</span>
                                                                    )}
                                                                </span>
                                                                <span className="adv-user-role">
                                                                    {row.user?.role ?? ''}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="adv-table-progress-bar">
                                                            <div
                                                                className={`adv-table-progress-fill ${row.status}`}
                                                                style={{ width: `${row.progressPercent}%` }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {row.totalMinutesViewed}/
                                                        {selectedPlan?.minimumTimeRequired ?? 0} min
                                                    </td>
                                                    <td>{row.lastActivity ? formatDate(row.lastActivity) : '—'}</td>
                                                    <td>
                                                        {row.enrollment.nextDueDate
                                                            ? formatDate(row.enrollment.nextDueDate)
                                                            : '—'}
                                                    </td>
                                                    <td>
                                                        <span className={`adv-compliance-badge ${row.status}`}>
                                                            {getComplianceBadgeLabel(row.status)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="adv-action-icons">
                                                            <button
                                                                className="adv-action-icon"
                                                                title="View profile"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onNavigate({
                                                                        type: 'user-profile',
                                                                        userId: row.enrollment.userId,
                                                                    });
                                                                }}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="16"
                                                                    height="16"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                >
                                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                                    <circle cx="12" cy="12" r="3" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                className="adv-action-icon"
                                                                title="Print"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                }}
                                                            >
                                                                🖨
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div
                                        className="adv-view-all-link"
                                        onClick={() => onNavigate({ type: 'enrollment', planId: selectedPlan?.id })}
                                    >
                                        View all {planEnrollments.length} enrolled users →
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Right Column */}
                <div className="adv-right-column">
                    {/* Category Progress */}
                    <div className="adv-category-progress">
                        <div className="adv-category-progress-header">
                            <h3>Category Progress</h3>
                            {isDraftPlan && !isEditingCategories && (
                                <button
                                    className="adv-edit-categories-btn"
                                    onClick={startEditingCategories}
                                    title="Edit categories"
                                >
                                    ✎ Edit
                                </button>
                            )}
                            {isDraftPlan && isEditingCategories && (
                                <div className="adv-category-edit-actions">
                                    <button
                                        className="adv-cat-save-btn"
                                        onClick={saveCategories}
                                        disabled={isSavingCategories}
                                    >
                                        {isSavingCategories ? 'Saving...' : 'Save'}
                                    </button>
                                    <button
                                        className="adv-cat-cancel-btn"
                                        onClick={cancelEditingCategories}
                                        disabled={isSavingCategories}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="adv-category-progress-body">
                            {isDraftPlan && isEditingCategories ? (
                                <>
                                    {editingCategories!.map((cat) => (
                                        <div key={cat.id} className="adv-category-edit-row">
                                            <input
                                                type="text"
                                                className="adv-cat-name-input"
                                                placeholder="Category name"
                                                value={cat.name}
                                                onChange={(e) => updateEditCategory(cat.id, 'name', e.target.value)}
                                            />
                                            <div className="adv-cat-minutes-input-group">
                                                <input
                                                    type="number"
                                                    className="adv-cat-minutes-input"
                                                    min={0}
                                                    placeholder="0"
                                                    value={cat.targetMinutes || ''}
                                                    onChange={(e) =>
                                                        updateEditCategory(
                                                            cat.id,
                                                            'targetMinutes',
                                                            Math.max(0, parseInt(e.target.value, 10) || 0),
                                                        )
                                                    }
                                                />
                                                <span className="adv-cat-minutes-label">min</span>
                                            </div>
                                            <label className="adv-cat-quiz-toggle" title="Require completion quiz">
                                                <input
                                                    type="checkbox"
                                                    checked={cat.requiresQuiz || false}
                                                    onChange={(e) =>
                                                        updateEditCategory(cat.id, 'requiresQuiz', e.target.checked)
                                                    }
                                                />
                                                <span>Quiz</span>
                                            </label>
                                            {cat.requiresQuiz && (
                                                <button
                                                    className="adv-cat-quiz-btn"
                                                    onClick={() => setQuizBuilderCategory(cat)}
                                                    title={cat.quizFormId ? 'Edit quiz' : 'Create quiz'}
                                                >
                                                    {cat.quizFormId ? '✎' : '+'}
                                                </button>
                                            )}
                                            <button
                                                className="adv-cat-delete-btn"
                                                onClick={() => removeEditCategory(cat.id)}
                                                title="Remove category"
                                                aria-label={`Remove category ${cat.name || 'unnamed'}`}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                    <button className="adv-cat-add-btn" onClick={addEditCategory}>
                                        + Add Category
                                    </button>
                                </>
                            ) : categories.length > 0 ? (
                                categories.map((cat) => {
                                    const progress = categoryProgress.find((cp) => cp.categoryId === cat.id);
                                    const contentStats = categoryContentStats.find((cs) => cs.categoryId === cat.id);
                                    const pct = isDraftPlan ? 0 : (progress?.percentage ?? 0);
                                    const completed = isDraftPlan ? 0 : (progress?.completedMinutes ?? 0);
                                    const assignedMin = contentStats?.assignedMinutes ?? 0;
                                    const unscheduledCount = contentStats?.unscheduledCount ?? 0;
                                    const assignedPct = contentStats?.assignedPct ?? 0;
                                    const scheduledPct = contentStats?.scheduledPct ?? 0;
                                    return (
                                        <div key={cat.id} className="adv-category-row">
                                            <div className="adv-category-row-header">
                                                <span className="adv-category-name">{cat.name}</span>
                                                <span className="adv-category-minutes">
                                                    {isActivePlan
                                                        ? `${completed}/${cat.targetMinutes} min`
                                                        : `${assignedMin}/${cat.targetMinutes} min`}
                                                </span>
                                            </div>
                                            {/* Content assigned progress bar (always visible) */}
                                            <div className="adv-progress-bar-track">
                                                {isDraftPlan ? (
                                                    <>
                                                        {/* Layered: scheduled (darker) on top of assigned (lighter) */}
                                                        <div
                                                            className="adv-progress-bar-fill assigned"
                                                            style={{
                                                                width: `${Math.min(100, Math.round(assignedPct))}%`,
                                                            }}
                                                        />
                                                        <div
                                                            className={`adv-progress-bar-fill scheduled ${scheduledPct >= 100 ? 'complete' : ''}`}
                                                            style={{
                                                                width: `${Math.min(100, Math.round(scheduledPct))}%`,
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                bottom: 0,
                                                            }}
                                                        />
                                                    </>
                                                ) : (
                                                    <div
                                                        className={`adv-progress-bar-fill ${pct >= 100 ? 'complete' : ''}`}
                                                        style={{ width: `${Math.min(100, Math.round(pct))}%` }}
                                                    />
                                                )}
                                            </div>
                                            {/* Content status detail line */}
                                            <div className="adv-category-content-stats">
                                                {assignedMin > 0 && (
                                                    <span className="adv-content-stat-assigned">
                                                        {contentStats?.assignedTopicCount ?? 0} topic
                                                        {(contentStats?.assignedTopicCount ?? 0) !== 1 ? 's' : ''}{' '}
                                                        assigned
                                                    </span>
                                                )}
                                                {assignedMin > 0 && unscheduledCount > 0 && (
                                                    <span className="adv-content-stat-unscheduled">
                                                        · {unscheduledCount} unscheduled
                                                    </span>
                                                )}
                                                {assignedMin > 0 &&
                                                    unscheduledCount === 0 &&
                                                    assignedMin < cat.targetMinutes && (
                                                        <span className="adv-content-stat-needs-more">
                                                            · Assign more topics to meet target
                                                        </span>
                                                    )}
                                                {assignedMin > 0 &&
                                                    unscheduledCount === 0 &&
                                                    assignedMin >= cat.targetMinutes && (
                                                        <span className="adv-content-stat-all-scheduled">
                                                            · All scheduled ✓
                                                        </span>
                                                    )}
                                                {assignedMin === 0 && (
                                                    <span className="adv-content-stat-none">No content assigned</span>
                                                )}
                                            </div>
                                            <div className="adv-category-quiz-controls">
                                                <label className="adv-cat-quiz-toggle" title="Require completion quiz">
                                                    <input
                                                        type="checkbox"
                                                        checked={cat.requiresQuiz || false}
                                                        onChange={async (e) => {
                                                            if (!selectedPlan) return;
                                                            // If unchecking and there's an existing quiz, show confirmation
                                                            if (!e.target.checked && cat.quizFormId) {
                                                                setConfirmDisableQuizCategory(cat);
                                                                return;
                                                            }
                                                            const updatedCats = categories.map((c) =>
                                                                c.id === cat.id
                                                                    ? { ...c, requiresQuiz: e.target.checked }
                                                                    : c,
                                                            );
                                                            await updatePlan({
                                                                id: selectedPlan.id,
                                                                categories: JSON.stringify(updatedCats),
                                                            });
                                                        }}
                                                    />
                                                    <span>Quiz</span>
                                                </label>
                                                {cat.requiresQuiz && (
                                                    <button
                                                        className="adv-cat-quiz-btn"
                                                        onClick={() => setQuizBuilderCategory(cat)}
                                                        title={cat.quizFormId ? 'Edit quiz' : 'Create quiz'}
                                                    >
                                                        {cat.quizFormId ? '✎ Edit Quiz' : '+ Create Quiz'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div style={{ color: 'var(--slate-gray)', fontSize: '13px', fontStyle: 'italic' }}>
                                    No categories defined
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Daily Briefing topic preview (same viewer as /daily-briefing) */}
            <LaunchDisplayModal
                visible={showContentViewer}
                topic={briefingTodayTopic}
                onDismiss={() => setShowContentViewer(false)}
            />

            {/* Quiz Builder Modal */}
            {quizBuilderCategory && selectedPlan && (
                <QuizBuilderModal
                    visible={!!quizBuilderCategory}
                    onDismiss={() => setQuizBuilderCategory(null)}
                    category={quizBuilderCategory}
                    companyId={companyId}
                    planId={selectedPlan.id}
                    onQuizSaved={async (categoryId, quizFormId) => {
                        const updatedCats = categories.map((c) => (c.id === categoryId ? { ...c, quizFormId } : c));
                        await updatePlan({
                            id: selectedPlan.id,
                            categories: JSON.stringify(updatedCats),
                        });
                        if (editingCategories) {
                            setEditingCategories((prev) => {
                                if (!prev) return prev;
                                return prev.map((c) => (c.id === categoryId ? { ...c, quizFormId } : c));
                            });
                        }
                        setQuizBuilderCategory(null);
                    }}
                />
            )}

            {/* Confirm Disable Quiz Modal */}
            {confirmDisableQuizCategory && (
                <div className="dtp-overlay" onClick={() => setConfirmDisableQuizCategory(null)}>
                    <div className="dtp-popup" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ padding: '24px' }}>
                            <h3 style={{ margin: '0 0 12px' }}>Disable Quiz?</h3>
                            <p style={{ fontSize: '14px', color: '#475569', margin: '0 0 20px' }}>
                                The category <strong>&ldquo;{confirmDisableQuizCategory.name}&rdquo;</strong> has an
                                existing quiz. Disabling it will stop requiring users to complete the quiz for this
                                category. The quiz form will not be deleted and can be re-enabled later.
                            </p>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button
                                    className="adv-cat-cancel-btn"
                                    onClick={() => setConfirmDisableQuizCategory(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="adv-cat-save-btn"
                                    style={{ background: '#dc2626' }}
                                    onClick={async () => {
                                        if (!selectedPlan) return;
                                        const updatedCats = categories.map((c) =>
                                            c.id === confirmDisableQuizCategory.id ? { ...c, requiresQuiz: false } : c,
                                        );
                                        await updatePlan({
                                            id: selectedPlan.id,
                                            categories: JSON.stringify(updatedCats),
                                        });
                                        setConfirmDisableQuizCategory(null);
                                    }}
                                >
                                    Disable Quiz
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Plan Confirmation Modal */}
            <DeletePlanConfirmModal
                isOpen={showDeleteModal}
                planName={selectedPlan?.name || ''}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={async () => {
                    if (selectedPlan) {
                        await deletePlan(selectedPlan.id);
                        setShowDeleteModal(false);
                    }
                }}
            />
        </>
    );
};
